import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { CLEARBOOK_BOOKING_URL } from "@/lib/clearbook";

const NAV = [
  { to: "/", label: "Forside" },
  { to: "/om-os", label: "Om os" },
];

const YDELSER = [
  { to: "/ydelser", label: "Alle ydelser", overview: true },
  { to: "/ydelser/rottebekaempelse", label: "Rottebekæmpelse" },
  { to: "/ydelser/kloakopgaver", label: "Kloakopgaver" },
  { to: "/ydelser/serviceaftale", label: "Serviceaftale" },
  { to: "/ydelser/no-dig", label: "No-dig" },
  { to: "/ydelser/spul-rens", label: "Spul & Rens" },
];

const SEGMENTS = [
  { to: "/forsikring", label: "Forsikring" },
  { to: "/hovedentreprenoerer", label: "Hovedentreprenører" },
  { to: "/forsyninger", label: "Forsyninger" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [ydelserOpen, setYdelserOpen] = useState(false);
  const [segOpen, setSegOpen] = useState(false);
  const { pathname } = useLocation();
  const forceSolid = pathname === "/book";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-[13px] tracking-[0.18em] uppercase font-light transition-colors ${
      isActive
        ? "text-[color:var(--fk-gold)]"
        : "text-white/70 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || forceSolid
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-farumkloak.png"
            alt="Farum Kloak"
            className="h-8 md:h-9 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              end={n.to === "/"}
              className={linkCls}
            >
              {n.label}
            </NavLink>
          ))}

          {/* Ydelser dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setYdelserOpen(true)}
            onMouseLeave={() => setYdelserOpen(false)}
          >
            <NavLink
              to="/ydelser"
              data-testid="nav-link-ydelser"
              className={({ isActive }) =>
                `flex items-center gap-1 text-[13px] tracking-[0.18em] uppercase font-light transition-colors ${
                  isActive
                    ? "text-[color:var(--fk-gold)]"
                    : "text-white/70 hover:text-white"
                }`
              }
            >
              Ydelser
              <ChevronDown className="w-3 h-3" />
            </NavLink>
            <div
              className={`absolute left-1/2 -translate-x-1/2 pt-5 transition-all duration-300 ${
                ydelserOpen
                  ? "opacity-100 visible"
                  : "opacity-0 invisible pointer-events-none"
              }`}
            >
              <div
                data-testid="nav-ydelser-dropdown"
                className="bg-black/95 backdrop-blur-xl border border-white/10 min-w-[260px] py-4"
              >
                {YDELSER.map((y) => (
                  <NavLink
                    key={y.to}
                    to={y.to}
                    end={y.overview}
                    className={({ isActive }) =>
                      `block px-6 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-colors ${
                        isActive
                          ? "text-[color:var(--fk-gold)]"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      } ${y.overview ? "border-b border-white/10 mb-2 pb-3" : ""}`
                    }
                  >
                    {y.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          {/* For hvem dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setSegOpen(true)}
            onMouseLeave={() => setSegOpen(false)}
          >
            <button
              data-testid="nav-link-for-hvem"
              className="flex items-center gap-1 text-[13px] tracking-[0.18em] uppercase font-light text-white/70 hover:text-white transition-colors"
            >
              For hvem
              <ChevronDown className="w-3 h-3" />
            </button>
            <div
              className={`absolute left-1/2 -translate-x-1/2 pt-5 transition-all duration-300 ${
                segOpen
                  ? "opacity-100 visible"
                  : "opacity-0 invisible pointer-events-none"
              }`}
            >
              <div
                data-testid="nav-for-hvem-dropdown"
                className="bg-black/95 backdrop-blur-xl border border-white/10 min-w-[260px] py-4"
              >
                {SEGMENTS.map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    className={({ isActive }) =>
                      `block px-6 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-colors ${
                        isActive
                          ? "text-[color:var(--fk-gold)]"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    {s.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/kontakt" data-testid="nav-link-kontakt" className={linkCls}>
            Kontakt
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={ASSETS.brand.phoneTel}
            data-testid="navbar-phone"
            className="flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase text-white/80 hover:text-[color:var(--fk-gold)] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {ASSETS.brand.phone}
          </a>
          <a
            href={CLEARBOOK_BOOKING_URL}
            data-testid="navbar-booking"
            className="px-5 py-3 text-[11px] tracking-[0.24em] uppercase border border-[color:var(--fk-gold)] text-[color:var(--fk-gold)] hover:bg-[color:var(--fk-gold)] hover:text-black transition-all duration-300"
          >
            Book opgave
          </a>
        </div>

        <button
          aria-label="Menu"
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.18em] uppercase text-white/80 hover:text-[color:var(--fk-gold)]"
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--fk-gold)] mb-3">
                Ydelser
              </div>
              <div className="flex flex-col gap-3 pl-3 border-l border-white/10">
                {YDELSER.map((y) => (
                  <NavLink
                    key={y.to}
                    to={y.to}
                    onClick={() => setOpen(false)}
                    className="text-sm tracking-[0.14em] uppercase text-white/70 hover:text-white"
                  >
                    {y.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--fk-gold)] mb-3">
                For hvem
              </div>
              <div className="flex flex-col gap-3 pl-3 border-l border-white/10">
                {SEGMENTS.map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    onClick={() => setOpen(false)}
                    className="text-sm tracking-[0.14em] uppercase text-white/70 hover:text-white"
                  >
                    {s.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <NavLink
              to="/kontakt"
              onClick={() => setOpen(false)}
              className="text-sm tracking-[0.18em] uppercase text-white/80 hover:text-[color:var(--fk-gold)] pt-2"
            >
              Kontakt
            </NavLink>
            <a
              href={ASSETS.brand.phoneTel}
              className="text-sm tracking-[0.18em] uppercase text-[color:var(--fk-gold)] pt-4 border-t border-white/10"
            >
              Døgnvagt · {ASSETS.brand.phone}
            </a>
            <a
              href={CLEARBOOK_BOOKING_URL}
              className="inline-block px-5 py-3 text-[11px] tracking-[0.24em] uppercase border border-[color:var(--fk-gold)] text-[color:var(--fk-gold)] text-center"
            >
              Book opgave
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
